import { useCallback, useRef, useState, useEffect } from 'react'
import {
  REQUIREMENT_DOC_SECTIONS,
  DEFAULT_REQUIREMENT_SECTION,
  type Requirement,
  type RequirementNode,
  type RequirementSection,
} from '@ispot/shared'
import type { CreateBlockInput } from './AdderBlock'

interface FlatRow {
  id: string
  depth: 0 | 1
  section: RequirementSection
}

interface UseOutlineHandlersArgs {
  requirements: Requirement[]
  grouped: Record<RequirementSection, RequirementNode[]>
  flatOrder: FlatRow[]
  detailId: string | null
  setDetailId: (id: string | null) => void
  updateRequirement: (id: string, patch: Partial<Requirement>) => Promise<unknown>
  deleteRequirement: (id: string) => Promise<void>
  createBlock: (input: CreateBlockInput) => Promise<{ id: string }>
  reorderSiblings: (parentId: string | null, orderedIds: string[]) => Promise<void>
}

function epicSection(item: Pick<Requirement, 'metadata'>): RequirementSection {
  const s = item.metadata?.section as RequirementSection | undefined
  return s && REQUIREMENT_DOC_SECTIONS.includes(s) ? s : DEFAULT_REQUIREMENT_SECTION
}

function focusRow(id: string, where: 'start' | 'end' = 'end') {
  requestAnimationFrame(() => {
    const el = document.querySelector<HTMLElement>(
      `[data-outline-id="${id}"] .outline-title`,
    )
    if (!el) return
    el.focus()
    el.scrollIntoView({ block: 'nearest' })
    const range = document.createRange()
    range.selectNodeContents(el)
    range.collapse(where !== 'start')
    const sel = window.getSelection()
    sel?.removeAllRanges()
    sel?.addRange(range)
  })
}

export function useOutlineHandlers(args: UseOutlineHandlersArgs) {
  const {
    requirements, grouped, flatOrder,
    detailId, setDetailId,
    updateRequirement, deleteRequirement,
    createBlock, reorderSiblings,
  } = args

  // Pending focus after a structural change (create / reorder / promote).
  const pendingFocusRef = useRef<{ id: string; where: 'start' | 'end' } | null>(null)
  useEffect(() => {
    const pending = pendingFocusRef.current
    if (!pending) return
    pendingFocusRef.current = null
    focusRow(pending.id, pending.where)
  })

  const handleCommit = useCallback(
    (id: string, patch: { name?: string; description?: string }) => {
      void updateRequirement(id, patch as Partial<Requirement>)
    },
    [updateRequirement],
  )

  const handleEnterAfter = useCallback(async (id: string) => {
    const current = requirements.find(r => r.id === id)
    if (!current) return
    const isEpic = current.type === 'epic' || current.type === 'nfr-category'
    const created = await createBlock({
      type: isEpic ? 'epic' : 'story',
      name: '',
      parentId: isEpic ? undefined : (current.parentId ?? undefined),
      section: isEpic ? epicSection(current) : undefined,
    })
    pendingFocusRef.current = { id: created.id, where: 'start' }
    try {
      const parentId = isEpic ? null : (current.parentId ?? null)
      const siblings = isEpic
        ? requirements.filter(
            r =>
              (r.type === 'epic' || r.type === 'nfr-category') &&
              epicSection(r) === epicSection(current),
          )
        : requirements.filter(r => r.parentId === parentId)
      const ids = siblings.map(s => s.id).filter(sid => sid !== created.id)
      const idx = ids.indexOf(id)
      const ordered = idx < 0
        ? [...ids, created.id]
        : [...ids.slice(0, idx + 1), created.id, ...ids.slice(idx + 1)]
      await reorderSiblings(parentId, ordered)
    } catch { /* noop: row was created, just appended at end */ }
  }, [requirements, createBlock, reorderSiblings])

  const handleIndent = useCallback(async (id: string) => {
    const item = requirements.find(r => r.id === id)
    if (!item) return
    if (item.type !== 'epic' && item.type !== 'nfr-category') return
    const section = epicSection(item)
    const sectionEpics = grouped[section] ?? []
    const idx = sectionEpics.findIndex(e => e.id === id)
    if (idx <= 0) return
    const prevEpic = sectionEpics[idx - 1]
    pendingFocusRef.current = { id, where: 'end' }
    await updateRequirement(id, {
      type: 'story',
      parentId: prevEpic.id,
      depth: 1,
    } as Partial<Requirement>)
  }, [requirements, grouped, updateRequirement])

  const handleOutdent = useCallback(async (id: string) => {
    const item = requirements.find(r => r.id === id)
    if (!item || item.type !== 'story') return
    const parent = requirements.find(r => r.id === item.parentId)
    const section: RequirementSection = parent ? epicSection(parent) : DEFAULT_REQUIREMENT_SECTION
    pendingFocusRef.current = { id, where: 'end' }
    await updateRequirement(id, {
      type: 'epic',
      parentId: null,
      depth: 0,
      metadata: { ...(item.metadata || {}), section },
    } as Partial<Requirement>)
  }, [requirements, updateRequirement])

  const handleDeleteIfEmpty = useCallback(async (id: string) => {
    const idx = flatOrder.findIndex(r => r.id === id)
    const prevId = idx > 0 ? flatOrder[idx - 1].id : null
    if (detailId === id) setDetailId(null)
    await deleteRequirement(id)
    if (prevId) focusRow(prevId, 'end')
  }, [flatOrder, deleteRequirement, detailId, setDetailId])

  const handleArrow = useCallback((id: string, dir: 1 | -1) => {
    const idx = flatOrder.findIndex(r => r.id === id)
    if (idx < 0) return
    const next = idx + dir
    if (next < 0 || next >= flatOrder.length) return
    const nextId = flatOrder[next].id
    focusRow(nextId, 'end')
    // When the detail pane is open, keep it in sync with the outline cursor.
    if (detailId !== null) setDetailId(nextId)
  }, [flatOrder, detailId, setDetailId])

  // Drag & drop ----------------------------------------------------------------
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [dropTarget, setDropTarget] = useState<{ id: string; edge: 'top' | 'bottom' } | null>(null)

  const handleDragStart = useCallback((id: string) => setDraggingId(id), [])
  const handleDragOver = useCallback((id: string, edge: 'top' | 'bottom') => {
    setDropTarget({ id, edge })
  }, [])
  const handleDragEnd = useCallback(() => {
    setDraggingId(null)
    setDropTarget(null)
  }, [])
  const handleDrop = useCallback(async (targetId: string) => {
    const sourceId = draggingId
    setDraggingId(null)
    const edge = dropTarget?.edge ?? 'bottom'
    setDropTarget(null)
    if (!sourceId || sourceId === targetId) return
    const source = requirements.find(r => r.id === sourceId)
    const target = requirements.find(r => r.id === targetId)
    if (!source || !target) return
    if (source.parentId !== target.parentId) return
    const parentId = source.parentId ?? null
    const siblings = requirements
      .filter(r => r.parentId === parentId)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(r => r.id)
    const without = siblings.filter(sid => sid !== sourceId)
    const tIdx = without.indexOf(targetId)
    const insertAt = edge === 'top' ? tIdx : tIdx + 1
    const next = [...without.slice(0, insertAt), sourceId, ...without.slice(insertAt)]
    await reorderSiblings(parentId, next)
  }, [draggingId, dropTarget, requirements, reorderSiblings])

  return {
    handleCommit,
    handleEnterAfter,
    handleIndent,
    handleOutdent,
    handleDeleteIfEmpty,
    handleArrow,
    handleAddBelow: handleEnterAfter,
    pendingFocusRef,
    draggingId,
    dropTarget,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDrop,
  }
}
